/**
 * Shared per-workout custom carb-target logic (Lane L7). ONE implementation of
 * "read / set / reset the journey's `customTargets.perWorkout[workoutId]`" so
 * the Edit-workout modal fuel rail and the FuelPlan Sessions view behave
 * identically.
 *
 * The override lives on the journey (keyed by PlannedWorkout.id), so a write is
 * a merge-PATCH of `customTargets` that must not clobber sibling keys
 * (`raceDayGramsPerHour`, `sodium`, or other workouts' overrides). Writes are
 * gated to ACTIVE journeys and real persisted workout ids — ADD mode passes
 * `workoutId=null` and every mutator becomes an explicit no-op that REJECTS
 * (never silently succeeds), so callers can surface an honest disabled state.
 */
import { useCallback, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthProvider";
import {
  patchJourney,
  notifyJourneyChanged,
  type CustomTargets,
  type JourneyView
} from "../../lib/journeyApi";
import { clampCarbTarget } from "../../lib/fuelClamps";

/**
 * Merge a per-workout override into a journey's `customTargets`, returning the
 * next object WITHOUT mutating the input. `value == null` deletes the key.
 * Pure — exported so the on-page provider's optimistic path reuses the exact
 * same merge the async PATCH sends.
 */
export function mergePerWorkoutTarget(
  current: CustomTargets | null | undefined,
  workoutId: string,
  value: number | null
): CustomTargets {
  const next: CustomTargets = { ...(current ?? {}) };
  const perWorkout: Record<string, number> = { ...(current?.perWorkout ?? {}) };
  if (value != null) perWorkout[workoutId] = value;
  else delete perWorkout[workoutId];
  if (Object.keys(perWorkout).length > 0) next.perWorkout = perWorkout;
  else delete next.perWorkout;
  return next;
}

/**
 * Clamp a raw g/hr input to the engine band and map the "unset" sentinel to
 * null (per-workout overrides model unset as null, `clampCarbTarget` returns 0).
 * Exported so callers can preview the value that would be saved.
 */
export function normalizeCarbTargetInput(value: number | null): number | null {
  const c = clampCarbTarget(value ?? 0);
  return c > 0 ? c : null;
}

export type UseCustomCarbTarget = {
  /** The persisted override for this workout, or null when auto. */
  value: number | null;
  /** True when no override is set (the plan uses its computed target). */
  isAuto: boolean;
  /** True only when a write can persist: active journey + real workout id. */
  canEdit: boolean;
  /** A PATCH is in flight. */
  saving: boolean;
  /**
   * Persist a custom g/hr target (clamped). Rejects when `!canEdit` — callers
   * must gate on `canEdit`, never rely on a silent success.
   */
  set: (gPerHour: number) => Promise<void>;
  /** Clear the override so the workout falls back to its auto target. */
  resetToAuto: () => Promise<void>;
};

/**
 * Read + write the per-workout custom carb target for a single workout.
 *
 * @param journey  the journey that owns this workout (already fetched by the
 *                 caller). Null ⇒ no journey ⇒ nothing to store ⇒ `canEdit=false`.
 * @param workoutId  PlannedWorkout.id, or null in ADD mode ⇒ `canEdit=false`.
 */
export function useCustomCarbTarget(
  journey: JourneyView | null | undefined,
  workoutId: string | null | undefined
): UseCustomCarbTarget {
  const { state, refreshCsrf } = useAuth();
  const csrfToken = (state as { csrfToken?: string }).csrfToken ?? "";
  const [saving, setSaving] = useState(false);

  const value = useMemo<number | null>(() => {
    if (!journey || !workoutId) return null;
    return journey.customTargets?.perWorkout?.[workoutId] ?? null;
  }, [journey, workoutId]);

  const canEdit = Boolean(
    journey && journey.status === "active" && workoutId && workoutId.length > 0
  );

  const write = useCallback(
    async (next: number | null): Promise<void> => {
      // NEVER silently succeed: an add-mode / no-journey / archived write has
      // nowhere to persist, so reject and let the UI show why.
      if (!journey || !workoutId || journey.status !== "active") {
        throw new Error("custom-target-not-editable");
      }
      const customTargets = mergePerWorkoutTarget(
        journey.customTargets,
        workoutId,
        next
      );
      setSaving(true);
      try {
        await patchJourney(journey.id, { customTargets }, csrfToken, refreshCsrf);
        // Signal every journey-bound surface (useJourney listeners) to refetch,
        // mirroring the on-page provider's `refresh()`.
        notifyJourneyChanged();
      } finally {
        setSaving(false);
      }
    },
    [journey, workoutId, csrfToken, refreshCsrf]
  );

  const set = useCallback(
    (gPerHour: number) => write(normalizeCarbTargetInput(gPerHour)),
    [write]
  );
  const resetToAuto = useCallback(() => write(null), [write]);

  return { value, isAuto: value == null, canEdit, saving, set, resetToAuto };
}
